import { PaymentMethod } from "@adyen/adyen-web/dist/types/types";
import type { PaymentServiceCore } from "@/types/adyen";
import RedirectElement from "@adyen/adyen-web/dist/types/components/Redirect/Redirect";
import { coupon } from "matchi-api";
import { tGlobal as t } from "i18n";
import { ReactNode } from "react";
import { PaymentMethodIcon } from "@/components";
import { sortByExpiryDate } from "@/utils/sortByExpiryDate";
import { emitPaymentServiceEvent } from "@/hooks";
import to from "await-to-js";

interface BasePaymentOption {
  id: string;
  type: PaymentType;
  title: string;
  description?: string;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface InternalPaymentOption extends BasePaymentOption {
  method: coupon;
}

export interface PaymentServiceOption extends BasePaymentOption {
  method: PaymentMethod;
  component: RedirectElement;
}

/**
 * Adyen marks stored payment methods with a `storedPaymentMethodId`.
 * The field isn't in their base PaymentMethod type, so we narrow it here.
 */
export const isNewCardMethod = (method: PaymentMethod | undefined) => {
  if (method?.type !== "scheme") return false;
  const { storedPaymentMethodId } = method as PaymentMethod & {
    storedPaymentMethodId?: string;
  };
  return !storedPaymentMethodId;
};

/**
 * Google Pay / Apple Pay. Adyen's wallet components don't render their
 * own "Save for my next payment" checkbox, so the checkout renders a
 * custom one when `enableRecurring` is true.
 */
export const isWalletMethod = (method: PaymentMethod | undefined) =>
  method?.type === "googlepay" || method?.type === "applepay";

const createPaymentServiceOptions = async (
  paymentService?: PaymentServiceCore,
): Promise<PaymentServiceOption[]> => {
  if (!paymentService) return [];

  // Filter out stored Google Pay tokens. When a shopper selects a stored
  // Google Pay token in one-click mode, Adyen converts recurringProcessingModel
  // to "CardOnFile" at transaction time (shopper-present semantics). Google Pay
  // rejects CardOnFile — it only accepts UnscheduledCardOnFile or Subscription —
  // so the payment is refused with "158 Required field 'MpiData' is not provided".
  // Adyen support confirmed this conflict and recommended filtering them out.
  // Users re-authorize Google Pay each checkout, which goes through a fresh flow.
  //
  // Stored Google Pay tokens come back as type: "scheme" with brand ending in
  // "_googlepay" (e.g., "visa_googlepay", "mc_googlepay", "amex_googlepay",
  // "maestro_googlepay") — not type: "googlepay".
  const storedPaymentMethods =
    paymentService.paymentMethodsResponse.storedPaymentMethods.filter(
      method => !method.brand?.endsWith("_googlepay"),
    );

  const methods = [
    ...storedPaymentMethods,
    ...paymentService.paymentMethodsResponse.paymentMethods,
  ];

  const options: PaymentServiceOption[] = [];

  // Read enableRecurring attached to the core by the paymentService query.
  // When true, force Adyen's card component to show the "Save for my next
  // payment" checkbox so the user can explicitly consent to storing the card.
  //
  // For non-recurring purchases, the backend controls `enableStoreDetails`
  // directly on the card payment method (shoppers can opt in to save for a
  // future one-off purchase); we deliberately don't override it here.
  const enableRecurring = !!paymentService.enableRecurring;

  // Apple Pay supported-networks alignment.
  //
  // Adyen's /sessions setup response returns a currency-filtered `brands` array
  // for the `scheme` (cards) and `googlepay` methods — e.g. ["visa","maestro","mc"]
  // for a GBP order, where American Express has no active acquirer route — but it
  // returns NO `brands` for the `applepay` method. adyen-web then falls back to its
  // built-in default ApplePay supportedNetworks, which includes "amex"
  // (ApplePay defaultProps: ['amex','discover','masterCard','visa']). The shopper
  // can pick Amex in the Apple Pay sheet, but the transaction is declined with
  // 905_1 ("Could not find an acquirer account") and we're charged a fee.
  //
  // Reuse the brands Adyen already currency-filters for cards as Apple Pay's
  // allowed networks (applied below): Amex shows in the wallet only where it's
  // accepted for direct card entry (e.g. SEK) and never where it isn't (e.g. GBP),
  // with no hardcoded per-market list. Google Pay is left untouched — Adyen sends
  // it correct `brands` already.
  const cardBrands = paymentService.paymentMethodsResponse.paymentMethods.find(
    m => m.type === "scheme" && Array.isArray(m.brands),
  )?.brands;

  for (const [index, method] of methods.entries()) {
    const config: Record<string, unknown> = {};
    if (method.type === "scheme") {
      // This gives challenge window (BankID) 100%x100% to fill the entire space of the iframe.
      // On a mobile device, this will prevent the overflow with horizontal scrolling.
      config.challengeWindowSize = "05";
      if (enableRecurring) {
        config.enableStoreDetails = true;
        // Adyen fires onChange whenever the component state changes —
        // including when the user toggles the "Save for my next payment"
        // checkbox. Forward storePaymentMethod via emitPaymentServiceEvent
        // so PaymentForm can react without polling the DOM.
        config.onChange = (state: {
          data?: { storePaymentMethod?: boolean };
        }) => {
          emitPaymentServiceEvent("onStorePaymentMethodChange", {
            storePaymentMethod: !!state?.data?.storePaymentMethod,
          });
        };
      }
    }
    // Pass `brands` (not `supportedNetworks`) so adyen-web maps Adyen brand
    // codes to Apple Pay networks (e.g. "mc" -> "masterCard"). The Adyen
    // `applepay` method carries no `brands` of its own today, so we backfill
    // from the card brands. The `!method.brands?.length` guard means that if
    // Adyen ever starts returning `applepay.brands`, we defer to those instead
    // of overriding them. See cardBrands above.
    if (
      method.type === "applepay" &&
      !method.brands?.length &&
      cardBrands?.length
    ) {
      config.brands = cardBrands;
    }
    const component = paymentService.create(method.type, {
      ...method,
      ...config,
    });

    // Components are typed incorrectly by Adyen, some methods like Apple Pay and Google Pay do have isAvailable method on them.
    // @see https://docs.adyen.com/payment-methods/apple-pay/web-component/#step-3-mount-the-component
    // @ts-expect-error - isAvailable exists on ApplePay/GooglePay but is not in the base component type
    if (component.isAvailable) {
      // @ts-expect-error - isAvailable exists on ApplePay/GooglePay but is not in the base component type
      const [err, isAvailable] = await to(component.isAvailable());
      if (err || !isAvailable) continue;
    }

    // Prevent core.update() from calling update() on our components.
    // core.update() fires during gift card order flows and calls
    // update() — unmount().mount(_node) on ALL registered components,
    // which corrupts iframe state and breaks subsequent 3DS form submission.
    // Our component lifecycle is fully managed by React (PaymentOption.tsx)
    // — mount/unmount/recreate happen through React state, not the core.
    (component as any).update = function () {
      return this;
    };

    options.push({
      id: `PAYMENT_SERVICE-${index}`,
      type: "PAYMENT_SERVICE" as const,
      title: method.name,
      icon: <PaymentMethodIcon type="PAYMENT_SERVICE" method={method} />,
      method,
      component,
    });
  }

  return options;
};

export const createCouponOptions = (
  coupons: coupon[] = [],
): InternalPaymentOption[] => {
  const options = sortByExpiryDate(coupons).map((method, index) => ({
    id: `COUPON-${method.method}-${index}`,
    type: "COUPON" as const,
    title: method.name,
    description: method.unlimited
      ? t("Unlimited")
      : t("{punches} punches remaining ({duration} min)", {
          punches: method.punches,
          duration: method.maxDuration,
        }),
    icon: <PaymentMethodIcon type={"COUPON"} />,
    // If coupon is unlimited, it's usable.
    // If not, check that punches and punchesNeeded are set and disable if punches is less than punchesNeeded.
    disabled: method.unlimited
      ? false
      : method?.punches && method?.punchesNeeded
        ? parseInt(method.punches) < parseInt(method.punchesNeeded)
        : false,
    method,
  }));

  return options;
};

export type MixedPaymentOption = InternalPaymentOption | PaymentServiceOption;

// Display order for the payment option list.
//
// Adyen returns a session's payment methods in whatever order the merchant
// account configuration produces — it varies by facility and currency — so
// these three land wherever that configuration happens to put them. Product
// wants them steered to the top: Apple Pay converts best for us, then Google
// Pay, then Swish.
const METHOD_DISPLAY_PRIORITY: Record<string, number> = {
  applepay: 0,
  googlepay: 1,
  swish: 2,
};

// Anything without a bucket sorts after every prioritised method. The sort is
// stable, so those keep the order they were built in — stored cards, then
// Adyen's own order.
const UNPRIORITISED = Number.MAX_SAFE_INTEGER;

const displayRank = (option: MixedPaymentOption) =>
  option.type === "PAYMENT_SERVICE"
    ? (METHOD_DISPLAY_PRIORITY[(option as PaymentServiceOption).method.type] ??
      UNPRIORITISED)
    : // Internal options — prepaid credit, and free orders if they ever render
      // here — stay above the whole Adyen block, as they do today.
      -1;

/**
 * Display order only, deliberately separate from the order of the array itself.
 *
 * The array `createPaymentOptions` returns is in *preference* order and stays
 * that way: PaymentForm pre-selects its first enabled entry, so reordering it
 * would also change what the checkout opens on — explicitly out of scope for
 * this change, and relied on by webapp-e2e (scenarios that type a CVC or a card
 * number straight into the Adyen iframe without selecting a method first).
 */
export const sortForDisplay = (options: MixedPaymentOption[]) =>
  [...options].sort((a, b) => displayRank(a) - displayRank(b));

export const createPaymentOptions = async (
  coupons?: coupon[],
  paymentService?: PaymentServiceCore,
): Promise<MixedPaymentOption[]> => {
  const couponOptions = createCouponOptions(coupons);
  const paymentServiceOptions =
    await createPaymentServiceOptions(paymentService);

  return [...couponOptions, ...paymentServiceOptions];
};

export const getPaymentOptionsQueryKey = ["paymentOptions"];

export const getPaymentOptionsQuery = (
  coupons?: coupon[],
  paymentService?: PaymentServiceCore,
) => ({
  queryKey: getPaymentOptionsQueryKey,
  queryFn: () => createPaymentOptions(coupons, paymentService),
});
